import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const CountdownCircle = ({ timeRemaining, totalDuration }) => {
    const RADIUS = 70;
    const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

    const percentage = totalDuration > 0 ? (timeRemaining / totalDuration) * 100 : 0;
    const offset = CIRCUMFERENCE - (percentage / 100) * CIRCUMFERENCE;

    const getColor = () => {
        const hours = timeRemaining / 3600;
        const minutes = timeRemaining / 60;
        if (minutes <= 5) return '#E53E3E'; // Red
        if (hours <= 1) return '#D69E2E'; // Yellow
        if (hours <= 12) return '#38A169'; // Green
        return '#5A67D8'; // Indigo
    };

    const strokeColor = getColor();

    return (
        <svg width="160" height="160" viewBox="0 0 160 160" className="transform -rotate-90">
            {/* Background Circle */}
            <circle
                cx="80"
                cy="80"
                r={RADIUS}
                stroke="#2D3748" // gray-800
                strokeWidth="10"
                fill="transparent"
            />
            {/* Progress Circle */}
            <circle
                cx="80"
                cy="80"
                r={RADIUS}
                stroke={strokeColor}
                strokeWidth="10"
                fill="transparent"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={offset}
                strokeLinecap="round"
                style={{
                    transition: 'stroke-dashoffset 0.5s linear, stroke 0.5s ease'
                }}
            />
        </svg>
    );
};

const CosmicCountdown = ({ drawDate, onComplete }) => {
    const { t } = useTranslation();
    const calculateTimeRemaining = () => {
        const now = new Date();
        const draw = new Date(drawDate);
        const totalSeconds = Math.max((draw - now) / 1000, 0);
        return totalSeconds;
    };

    const [timeRemaining, setTimeRemaining] = useState(calculateTimeRemaining());
    // Assuming a 24-hour lottery cycle for the progress visualization
    const totalDuration = 24 * 60 * 60;

    useEffect(() => {
        if (timeRemaining <= 0) {
            if (onComplete) {
                onComplete();
            }
            return;
        }

        const interval = setInterval(() => {
            setTimeRemaining(prev => {
                const newTime = Math.max(prev - 1, 0);
                if (newTime <= 10 && newTime > 0) {
                    // TODO: Add haptic heartbeat and ticking clock sound for final 10 seconds
                    // playSound('countdown-tick');
                    // triggerHaptics('light');
                }
                if (newTime === 0 && onComplete) {
                    onComplete();
                }
                return newTime;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [timeRemaining, onComplete]);

    const formatTime = (totalSeconds) => {
        const hours = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
        const minutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
        const seconds = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    return (
        <div className="relative flex flex-col items-center justify-center my-4">
            <CountdownCircle timeRemaining={timeRemaining} totalDuration={totalDuration} />
            <div className="absolute flex flex-col items-center">
                <div className="text-4xl font-bold text-white celestial-numbers">
                    {formatTime(timeRemaining)}
                </div>
                <div className="text-sm text-gray-400 tracking-widest">{t('cosmicCountdown.untilDraw')}</div>
            </div>
        </div>
    );
};

export default CosmicCountdown;
